/**
 * TRANG CHỦ.
 *
 * The calm screen. It answers, in order, the five questions an employee
 * actually opens the app to ask:
 *
 *   1. Am I clocked in, and how long have I worked?      (live, server-anchored)
 *   2. Is anything urgent waiting for me?                 (offer banner)
 *   3. What am I doing right now?                         (current task)
 *   4. How am I doing today?                              (KPI + rank + pay)
 *   5. What do I tap next?                                (three big actions)
 *
 * and NOTHING ELSE. Every other feature in the app lives behind one of the five
 * tabs. The spec explicitly calls out a long page of dozens of buttons as the
 * failure mode, so anything that is not one of those five answers does not
 * belong here — it belongs in Cá nhân.
 *
 * The three primary actions sit in a fixed dock in the bottom third of the
 * screen, above the nav bar, so they are reachable with a thumb without
 * re-gripping the phone.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHome, errorMessage, queryKeys } from '../lib/queries';
import { useServerClock } from '../lib/hooks';
import { api } from '../lib/api';
import {
  formatClock,
  formatCountdown,
  formatMinutes,
  formatTime,
  formatBusinessDate,
} from '../lib/format';
import {
  PRESENCE_LABEL,
  PRESENCE_TONE,
  TASK_STATUS_LABEL,
  TASK_STATUS_TONE,
  dayStatusLabel,
  taskTypeLabel,
  urgencyLabel,
  urgencyTone,
} from '../lib/labels';
import type { PresenceStatus } from '../lib/contracts';
import {
  Banner,
  Button,
  Card,
  ErrorState,
  KeyValue,
  LoadingState,
  MoneyText,
  SectionHeader,
  Sheet,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../components/Toast';

export function HomeScreen(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useHome();
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);

  // The displayed clock is anchored to the SERVER's `serverTime`, advanced by a
  // monotonic timer. A phone whose clock is twenty minutes fast still shows the
  // same worked time the payslip will.
  const now = useServerClock(data?.serverTime, 1000);

  const setStatus = useMutation({
    mutationFn: (status: PresenceStatus) => api.setStatus({ status }),
    onSuccess: (result) => {
      toast.success(`Đã cập nhật: ${PRESENCE_LABEL[result.status]}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.home });
      setStatusSheetOpen(false);
    },
    onError: (e) => toast.error('Không đổi được trạng thái', errorMessage(e)),
  });

  if (isLoading) return <LoadingState label="Đang tải trang chủ…" />;
  if (isError || data === undefined) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const { attendance, presence, currentShift, currentTask, todayKpi, rank, provisionalPay } = data;

  // Worked seconds = the server's rollup (which already includes the open
  // stretch up to serverTime) + the seconds that have ticked by since.
  const workedSeconds =
    attendance.workedMinutes * 60 + (attendance.checkedIn ? Math.max(0, Math.floor((now - data.serverTime) / 1000)) : 0);

  const mostUrgent = data.urgentTasks[0] ?? null;

  return (
    <div className="space-y-4 pb-40">
      {/* --- who / where / when ------------------------------------------- */}
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-bold text-slate-900">{data.employee.name}</p>
            <p className="text-sm text-slate-500">
              Mã NV {data.employee.code} · {formatBusinessDate(data.businessDate)}
            </p>
          </div>
          <StatusBadge tone={PRESENCE_TONE[presence.status]}>{PRESENCE_LABEL[presence.status]}</StatusBadge>
        </div>

        <div className="mt-3 rounded-2xl bg-slate-50 p-3">
          {currentShift === null ? (
            <p className="text-sm text-slate-600">Hôm nay bạn không có ca làm được phân công.</p>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-800">
                {currentShift.name ?? 'Ca làm việc'}
                {currentShift.isShiftLead ? ' · Trưởng ca' : ''}
              </p>
              <p className="text-sm text-slate-600">
                {formatTime(currentShift.startAt)} – {formatTime(currentShift.endAt)}
                {currentShift.minutesUntilStart > 0
                  ? ` · bắt đầu sau ${formatMinutes(currentShift.minutesUntilStart)}`
                  : ''}
              </p>
            </>
          )}
        </div>
      </Card>

      {/* --- attendance + live worked time -------------------------------- */}
      <Card onClick={() => navigate('/cham-cong')}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-slate-500">Thời gian đã làm hôm nay</p>
            {/* aria-live off: a per-second announcement would be unusable. */}
            <p className="mt-0.5 text-4xl font-black tabular-nums text-slate-900">
              {formatClock(workedSeconds)}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {attendance.checkedIn ? 'Đang trong ca' : 'Chưa vào ca / đã tan ca'} ·{' '}
              {dayStatusLabel(attendance.dayStatus)}
            </p>
          </div>
          <StatusBadge tone={attendance.checkedIn ? 'success' : 'neutral'}>
            {attendance.checkedIn ? 'Đã vào ca' : 'Ngoài ca'}
          </StatusBadge>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2 text-center text-sm">
          <div>
            <p className="text-slate-500">Vào ca</p>
            <p className="font-semibold tabular-nums">{formatTime(attendance.firstInAt)}</p>
          </div>
          <div>
            <p className="text-slate-500">Nghỉ giữa ca</p>
            <p className="font-semibold tabular-nums">{formatMinutes(attendance.breakMinutes)}</p>
          </div>
          <div>
            <p className="text-slate-500">Đi muộn</p>
            <p className="font-semibold tabular-nums">{formatMinutes(attendance.lateMinutes)}</p>
          </div>
        </div>
        <p className="mt-2 text-center text-xs font-semibold text-brand-700">Chạm để mở màn hình chấm công →</p>
      </Card>

      {/* --- urgent offer ------------------------------------------------- */}
      {mostUrgent === null ? null : (
        <Banner
          tone="danger"
          title={`Việc gấp: ${mostUrgent.title}`}
          action={
            <Button variant="danger" size="lg" fullWidth onClick={() => navigate('/cong-viec')}>
              Xem và nhận ngay
            </Button>
          }
        >
          {taskTypeLabel(mostUrgent.type)} · còn{' '}
          <strong className="tabular-nums">
            {formatCountdown(Math.max(0, Math.floor((mostUrgent.offerExpiresAt - now) / 1000)))}
          </strong>{' '}
          để phản hồi
          {data.urgentTasks.length > 1 ? ` · còn ${data.urgentTasks.length - 1} việc gấp khác` : ''}
        </Banner>
      )}

      {/* --- current task -------------------------------------------------- */}
      <div>
        <SectionHeader title="Việc đang làm" />
        {currentTask === null || currentTask.id === undefined ? (
          <Card>
            <p className="text-sm text-slate-600">
              Bạn chưa nhận việc nào. {data.offeredTaskCount > 0 ? `Có ${data.offeredTaskCount} việc đang chờ bạn nhận.` : ''}
            </p>
          </Card>
        ) : (
          <Card onClick={() => navigate(`/cong-viec/${currentTask.id ?? ''}`)}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-slate-900">{currentTask.title ?? 'Nhiệm vụ'}</p>
                <p className="mt-0.5 text-sm text-slate-500">{taskTypeLabel(currentTask.type ?? '')}</p>
                {currentTask.detail === null || currentTask.detail === undefined ? null : (
                  <p className="mt-1 line-clamp-2 text-sm text-slate-600">{currentTask.detail}</p>
                )}
              </div>
              {currentTask.status === undefined ? null : (
                <StatusBadge tone={TASK_STATUS_TONE[currentTask.status]}>
                  {TASK_STATUS_LABEL[currentTask.status]}
                </StatusBadge>
              )}
            </div>
            {currentTask.dueAt === null || currentTask.dueAt === undefined ? null : (
              <p className="mt-2 text-sm text-slate-600">
                Hạn: <strong className="tabular-nums">{formatTime(currentTask.dueAt)}</strong>
                {currentTask.urgency === undefined ? null : (
                  <>
                    {' · '}
                    <span className={urgencyTone(currentTask.urgency) === 'danger' ? 'text-rose-700' : ''}>
                      {urgencyLabel(currentTask.urgency)}
                    </span>
                  </>
                )}
              </p>
            )}
          </Card>
        )}
      </div>

      {/* --- KPI + rank + provisional pay ---------------------------------- */}
      <div>
        <SectionHeader
          title="Hôm nay của bạn"
          action={
            <Link to="/ca-nhan/kpi" className="text-sm font-semibold text-brand-700">
              Chi tiết KPI →
            </Link>
          }
        />
        <Card>
          {todayKpi === null ? (
            <p className="text-sm text-slate-600">
              Chưa có điểm KPI cho hôm nay. Điểm được tính sau khi hệ thống tổng hợp các thao tác của bạn.
            </p>
          ) : (
            <>
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className="text-sm text-slate-500">Điểm KPI hôm nay</p>
                  <p className="text-3xl font-black tabular-nums text-slate-900">
                    {(todayKpi.totalPointsMilli / 1000).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Xếp hạng chi nhánh</p>
                  <p className="text-2xl font-black tabular-nums text-brand-700">
                    {rank?.inBranch === null || rank?.inBranch === undefined ? '—' : `#${rank.inBranch}`}
                  </p>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2 text-center text-sm">
                <div>
                  <p className="text-slate-500">Việc</p>
                  <p className="font-semibold tabular-nums">{todayKpi.taskCount}</p>
                </div>
                <div>
                  <p className="text-slate-500">Đúng hạn</p>
                  <p className="font-semibold tabular-nums text-emerald-700">{todayKpi.onTimeCount}</p>
                </div>
                <div>
                  <p className="text-slate-500">Trễ hạn</p>
                  <p className="font-semibold tabular-nums text-rose-700">{todayKpi.lateCount}</p>
                </div>
              </div>
            </>
          )}
        </Card>

        {provisionalPay === null ? null : (
          <Card className="mt-3" onClick={() => navigate('/ca-nhan/luong-tam-tinh')}>
            <KeyValue
              label="Lương tạm tính kỳ này"
              hint={`${provisionalPay.periodStart} → ${provisionalPay.periodEnd}`}
              value={
                <MoneyText
                  amountMinor={provisionalPay.estimatedNetMinor}
                  currency={provisionalPay.currency}
                  className="text-xl"
                />
              }
            />
            <p className="text-xs text-slate-500">Số tạm tính, chưa gồm thưởng KPI và điều chỉnh. Chạm để xem chi tiết.</p>
          </Card>
        )}
      </div>

      {/* --- notifications summary ------------------------------------------ */}
      {data.unreadNotifications > 0 ? (
        <Banner
          tone="info"
          title={`${data.unreadNotifications} thông báo chưa đọc`}
          action={
            <Button variant="secondary" fullWidth onClick={() => navigate('/thong-bao')}>
              Mở thông báo
            </Button>
          }
        />
      ) : null}

      {/* --- THE THREE PRIMARY ACTIONS, in the bottom third ----------------- */}
      <div className="action-dock">
        <div className="grid grid-cols-3 gap-2">
          <Button size="lg" onClick={() => navigate('/quet-qr')} className="flex-col !gap-0.5 !px-2 text-sm">
            Quét QR
          </Button>
          <Button
            size="lg"
            variant={data.offeredTaskCount > 0 ? 'danger' : 'secondary'}
            onClick={() => navigate('/cong-viec')}
            className="flex-col !gap-0.5 !px-2 text-sm"
          >
            {data.offeredTaskCount > 0 ? `Nhận việc (${data.offeredTaskCount})` : 'Nhận việc'}
          </Button>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => setStatusSheetOpen(true)}
            className="flex-col !gap-0.5 !px-2 text-sm"
          >
            {presence.status === 'AVAILABLE' ? 'Báo bận' : 'Báo rảnh'}
          </Button>
        </div>
      </div>

      <StatusSheet
        open={statusSheetOpen}
        current={presence.status}
        pending={setStatus.isPending}
        onClose={() => setStatusSheetOpen(false)}
        onPick={(status) => setStatus.mutate(status)}
      />
    </div>
  );
}

/**
 * The presence picker.
 *
 * Only the statuses an employee may legitimately set for themselves are listed.
 * BUSY/SERVING_CUSTOMER/PROCESSING_PAYMENT are set by the system as a
 * consequence of accepting work, and EMERGENCY is deliberately last and red.
 */
const SELECTABLE: readonly { status: PresenceStatus; hint: string }[] = [
  { status: 'AVAILABLE', hint: 'Hệ thống sẽ điều phối việc mới cho bạn.' },
  { status: 'TEMPORARILY_UNAVAILABLE', hint: 'Tạm thời không nhận việc mới.' },
  { status: 'ON_BREAK', hint: 'Đang nghỉ giữa ca. Nhớ chấm công nghỉ giữa ca ở màn hình Chấm công.' },
  { status: 'AWAY_WITH_PERMISSION', hint: 'Rời vị trí có phép — quản lý sẽ thấy trạng thái này.' },
  { status: 'EMERGENCY', hint: 'Chỉ dùng khi có sự cố cần hỗ trợ ngay.' },
];

function StatusSheet({
  open,
  current,
  pending,
  onClose,
  onPick,
}: {
  readonly open: boolean;
  readonly current: PresenceStatus;
  readonly pending: boolean;
  readonly onClose: () => void;
  readonly onPick: (status: PresenceStatus) => void;
}): JSX.Element {
  return (
    <Sheet open={open} title="Cập nhật trạng thái" onClose={onClose}>
      <ul className="space-y-2">
        {SELECTABLE.map((item) => (
          <li key={item.status}>
            <button
              type="button"
              disabled={pending}
              onClick={() => onPick(item.status)}
              className={[
                'w-full rounded-2xl border p-3 text-left active:bg-slate-50 disabled:opacity-60',
                'min-h-touch-lg',
                item.status === current ? 'border-brand-500 bg-brand-50' : 'border-slate-200',
                item.status === 'EMERGENCY' ? 'border-rose-300' : '',
              ].join(' ')}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-bold text-slate-900">{PRESENCE_LABEL[item.status]}</span>
                {item.status === current ? <StatusBadge tone="info">Hiện tại</StatusBadge> : null}
              </span>
              <span className="mt-0.5 block text-sm text-slate-600">{item.hint}</span>
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}
